import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Task } from '../../../shared/types.ts';
import { PriorityBadge } from '../common/Badge.tsx';

interface CalendarViewProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({ tasks, onTaskClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Calculate calendar days
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const calendarCells = [];
  for (let i = 0; i < firstDayIndex; i++) {
    calendarCells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarCells.push(day);
  }

  const todayStr = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState<string>(todayStr);

  const selectedDateTasks = tasks.filter((t) => t.dueDate === selectedDate);
  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const selectedDateFormatted = isNaN(selectedDateObj.getTime())
    ? selectedDate
    : selectedDateObj.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white dark:bg-surface-900 rounded-3xl shadow-card border border-surface-200 dark:border-surface-800 overflow-hidden font-sans">
      {/* Calendar Top Controls */}
      <div className="p-3.5 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
        <div className="flex items-center space-x-2 font-bold text-sm text-surface-900 dark:text-surface-100">
          <CalendarIcon className="w-4 h-4 text-brand-500" />
          <span>{monthName}</span>
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500 transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              const now = new Date();
              setCurrentDate(now);
              setSelectedDate(now.toISOString().split('T')[0]);
            }}
            className="px-2.5 py-1 text-xs rounded-lg font-medium hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-300"
          >
            Today
          </button>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500 transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Weekday Labels */}
      <div className="grid grid-cols-7 border-b border-surface-200 dark:border-surface-800 text-center text-[10px] sm:text-[11px] font-semibold text-surface-400 py-2 bg-surface-50 dark:bg-surface-800/40">
        <div>Sun</div>
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div>Sat</div>
      </div>

      {/* Desktop Calendar Grid (Hidden on Mobile) */}
      <div className="hidden sm:grid grid-cols-7 auto-rows-fr divide-x divide-y divide-surface-100 dark:divide-surface-800/80">
        {calendarCells.map((dayNum, idx) => {
          if (dayNum === null) {
            return <div key={`empty-${idx}`} className="h-28 bg-surface-50/40 dark:bg-surface-900/20" />;
          }

          const currentCellDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          const isToday = currentCellDate === todayStr;
          const dayTasks = tasks.filter((t) => t.dueDate === currentCellDate);

          return (
            <div
              key={dayNum}
              className={`h-28 p-1.5 overflow-y-auto transition-colors ${
                isToday ? 'bg-brand-50/20 dark:bg-brand-950/10' : 'hover:bg-surface-50/60 dark:hover:bg-surface-800/20'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-semibold inline-flex items-center justify-center w-5 h-5 rounded-full ${
                    isToday ? 'bg-brand-600 text-white' : 'text-surface-600 dark:text-surface-400'
                  }`}
                >
                  {dayNum}
                </span>
                {dayTasks.length > 0 && (
                  <span className="text-[10px] text-surface-400 font-medium">
                    {dayTasks.length} {dayTasks.length === 1 ? 'task' : 'tasks'}
                  </span>
                )}
              </div>

              <div className="space-y-1">
                {dayTasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onTaskClick(t)}
                    className="w-full text-left p-1 rounded bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-subtle hover:border-brand-500/50 transition-all block"
                  >
                    <div className="text-[10px] font-medium text-surface-900 dark:text-surface-100 truncate">
                      {t.title}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile-Friendly Compact Month Grid */}
      <div className="sm:hidden grid grid-cols-7 divide-x divide-y divide-surface-100 dark:divide-surface-800/60 border-b border-surface-200 dark:border-surface-800">
        {calendarCells.map((dayNum, idx) => {
          if (dayNum === null) {
            return <div key={`empty-m-${idx}`} className="h-11 bg-surface-50/30 dark:bg-surface-900/10" />;
          }

          const currentCellDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          const isToday = currentCellDate === todayStr;
          const isSelected = currentCellDate === selectedDate;
          const dayTasks = tasks.filter((t) => t.dueDate === currentCellDate);

          return (
            <button
              key={`m-${dayNum}`}
              onClick={() => setSelectedDate(currentCellDate)}
              className={`h-11 flex flex-col items-center justify-center p-0.5 relative transition-colors ${
                isSelected
                  ? 'bg-brand-500/15 dark:bg-brand-500/25 font-bold'
                  : isToday
                  ? 'bg-surface-100 dark:bg-surface-800/50'
                  : 'hover:bg-surface-50 dark:hover:bg-surface-800/20'
              }`}
            >
              <span
                className={`text-xs inline-flex items-center justify-center w-6 h-6 rounded-full ${
                  isSelected
                    ? 'bg-brand-600 text-white font-bold shadow-sm'
                    : isToday
                    ? 'bg-brand-100 dark:bg-brand-900/60 text-brand-700 dark:text-brand-300 font-bold'
                    : 'text-surface-800 dark:text-surface-200'
                }`}
              >
                {dayNum}
              </span>
              {dayTasks.length > 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {/* Mobile Agenda List for Selected Date */}
      <div className="sm:hidden p-3.5 space-y-2.5 bg-surface-50/50 dark:bg-surface-950/20">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-surface-900 dark:text-surface-100 flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-brand-500" />
            <span>{selectedDateFormatted}</span>
          </span>
          <span className="text-[11px] font-semibold text-surface-500">
            {selectedDateTasks.length} {selectedDateTasks.length === 1 ? 'task' : 'tasks'}
          </span>
        </div>

        {selectedDateTasks.length === 0 ? (
          <div className="py-6 text-center text-xs text-surface-400 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200/60 dark:border-surface-800">
            No tasks due on this date.
          </div>
        ) : (
          <div className="space-y-2">
            {selectedDateTasks.map((task) => (
              <div
                key={task.id}
                onClick={() => onTaskClick(task)}
                className="p-3 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200/80 dark:border-surface-800 shadow-sm flex items-center justify-between cursor-pointer active:scale-[0.99] transition-transform"
              >
                <div className="min-w-0 pr-2">
                  <div className="flex items-center space-x-1.5 mb-0.5">
                    {task.code && (
                      <span className="text-[9px] font-mono font-bold px-1.5 py-0.2 rounded bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 border border-surface-200 dark:border-surface-700">
                        {task.code}
                      </span>
                    )}
                    <PriorityBadge priority={task.priority} size="sm" />
                  </div>
                  <h4 className="text-xs font-semibold text-surface-900 dark:text-surface-100 truncate">
                    {task.title}
                  </h4>
                </div>

                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 shrink-0">
                  {task.status.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
